<!--
  Orderscm-det — assign-order modal for PM (custom-made) orders.
  Redesigned 2026-06-10 (v2.6.0, Phase 1.5 Chunk A.1).

  Two-pane modal:
    LEFT  — assign form (operator + priority + carrier + address +
            note). All fields native HTML; combobox = native <select>.
    RIGHT — products list with row checkbox + qty input + select-all
            toggle.

  Container is the Diakonix centered modal pattern: backdrop fixed at
  100vw/100dvh, card centered, close-X in header, footer with primary
  action button. Click on backdrop closes (goes back to /pm-orders).
-->

@if (opened) {
  <div class="modal-backdrop" (click)="goBack()">
    <aside class="modal modal--xl" (click)="$event.stopPropagation()">

      <!-- ─── Header ─────────────────────────────────────────────── -->
      <header class="modal__header">
        <h2 class="modal__title">
          ✏️ Assegnazione ordine PM
          @if (orderNew?.or_order_number) {
            <span class="modal__title-tag">n° {{ orderNew.or_order_number }}</span>
          }
        </h2>
        <button type="button" class="modal-close" (click)="goBack()" aria-label="Chiudi">×</button>
      </header>

      <!-- ─── Body — 2 panes ─────────────────────────────────────── -->
      <div class="modal__body">

        @if (!orderNew) {
          <div class="loading-card">
            <div class="loading-card__spinner"></div>
            <p>Caricamento ordine…</p>
          </div>
        } @else {
          <form
            class="assign-grid"
            [formGroup]="assignOrderForm"
            (ngSubmit)="addOrderMethod(assignOrderForm)"
          >

            <!-- ─── LEFT: assign form ─────────────────────────── -->
            <section class="assign-form" aria-label="Dati assegnazione">

              <div class="form-grid">

                <div class="form-field">
                  <label class="form-field__label" for="or_order_number">N° Ordine</label>
                  <input
                    id="or_order_number"
                    type="text"
                    class="form-input form-input--readonly"
                    formControlName="or_order_number"
                    readonly
                  />
                </div>

                <div class="form-field">
                  <label class="form-field__label" for="or_order_date">Data ordine</label>
                  <input
                    id="or_order_date"
                    type="text"
                    class="form-input form-input--readonly"
                    formControlName="or_order_date"
                    readonly
                  />
                </div>

                <div class="form-field form-field--full">
                  <label class="form-field__label" for="or_customer">Cliente</label>
                  <input
                    id="or_customer"
                    type="text"
                    class="form-input form-input--readonly"
                    formControlName="or_customer"
                    readonly
                  />
                </div>

                <div class="form-field">
                  <label class="form-field__label" for="or_assigned_id">Operatore <span class="form-field__req">*</span></label>
                  <select
                    id="or_assigned_id"
                    class="form-input form-input--select"
                    formControlName="or_assigned_id"
                  >
                    <option value="" disabled>Seleziona…</option>
                    @for (w of workers; track w.account_id) {
                      <option [ngValue]="w.account_id">{{ w.full_name }}</option>
                    }
                  </select>
                  @if (
                    (assignOrderForm.get('or_assigned_id')?.touched ||
                     assignOrderForm.get('or_assigned_id')?.dirty) &&
                    assignOrderForm.get('or_assigned_id')?.errors
                  ) {
                    <p class="form-error">⚠️ Operatore non impostato</p>
                  }
                </div>

                <div class="form-field">
                  <label class="form-field__label" for="or_order_priority_cod">Priorità <span class="form-field__req">*</span></label>
                  <select
                    id="or_order_priority_cod"
                    class="form-input form-input--select"
                    formControlName="or_order_priority_cod"
                  >
                    <option value="" disabled>Seleziona…</option>
                    @for (p of priorityOptions; track p.tab_priority_order_cod) {
                      <option [ngValue]="p.tab_priority_order_cod">{{ p.tab_priority_order_desc }}</option>
                    }
                  </select>
                  @if (
                    (assignOrderForm.get('or_order_priority_cod')?.touched ||
                     assignOrderForm.get('or_order_priority_cod')?.dirty) &&
                    assignOrderForm.get('or_order_priority_cod')?.errors
                  ) {
                    <p class="form-error">⚠️ Priorità non impostata</p>
                  }
                </div>

                <div class="form-field form-field--full">
                  <label class="form-field__label" for="or_shipping_address">Indirizzo di consegna</label>
                  <input
                    id="or_shipping_address"
                    type="text"
                    class="form-input"
                    formControlName="or_shipping_address"
                  />
                </div>

                <div class="form-field">
                  <label class="form-field__label" for="or_shipping_city">Città</label>
                  <input
                    id="or_shipping_city"
                    type="text"
                    class="form-input"
                    formControlName="or_shipping_city"
                  />
                </div>

                <div class="form-field">
                  <label class="form-field__label" for="or_shipping_zip">CAP</label>
                  <input
                    id="or_shipping_zip"
                    type="text"
                    class="form-input"
                    formControlName="or_shipping_zip"
                  />
                </div>

                <div class="form-field">
                  <label class="form-field__label" for="or_shipping_prov">Provincia</label>
                  <input
                    id="or_shipping_prov"
                    type="text"
                    class="form-input"
                    formControlName="or_shipping_prov"
                  />
                </div>

                <div class="form-field">
                  <label class="form-field__label" for="or_shipping_country">Nazione</label>
                  <input
                    id="or_shipping_country"
                    type="text"
                    class="form-input"
                    formControlName="or_shipping_country"
                  />
                </div>

                <div class="form-field form-field--full">
                  <label class="form-field__label" for="carrier_id">Corriere <span class="form-field__req">*</span></label>
                  <select
                    id="carrier_id"
                    class="form-input form-input--select"
                    formControlName="carrier_id"
                  >
                    <option value="" disabled>Seleziona…</option>
                    @for (c of carriers; track c.carrier_id) {
                      <option [ngValue]="c.carrier_id">{{ c.carrier_desc }}</option>
                    }
                  </select>
                  @if (
                    (assignOrderForm.get('carrier_id')?.touched ||
                     assignOrderForm.get('carrier_id')?.dirty) &&
                    assignOrderForm.get('carrier_id')?.errors
                  ) {
                    <p class="form-error">⚠️ Corriere non impostato</p>
                  }
                </div>

                <div class="form-field form-field--full">
                  <label class="form-field__label" for="or_note">Note</label>
                  <input
                    id="or_note"
                    type="text"
                    class="form-input"
                    formControlName="or_note"
                    placeholder="—"
                  />
                </div>

              </div>

              @if (assignOrderForm.invalid && assignOrderForm.touched) {
                <p class="form-banner-error">
                  Compila tutti i campi obbligatori prima di assegnare.
                </p>
              }

            </section>

            <!-- ─── RIGHT: products list ───────────────────────── -->
            <section class="products-list" aria-label="Elenco prodotti">

              <header class="products-list__header">
                <h3 class="products-list__title">📦 Prodotti</h3>
                <button
                  type="button"
                  class="btn btn--ghost btn--sm"
                  (click)="selectAllProducts()"
                >
                  @if (areSelected) { ✖ Deseleziona tutti }
                  @else { ✓ Seleziona tutti }
                </button>
              </header>

              @if (productsNew.length === 0) {
                <p class="products-empty">Nessun prodotto associato a questo ordine.</p>
              } @else {
                <ul class="products-rows" role="list">
                  @for (prod of productsNew; track prod.op_code + '-' + ($index); let i = $index) {
                    <li
                      class="product-row"
                      [class.product-row--checked]="prod.op_checked"
                      [class.product-row--lowstock]="prod.op_qta > (prod.op_stock ?? 0)"
                    >
                      <label class="product-row__check">
                        <input
                          type="checkbox"
                          [checked]="prod.op_checked"
                          (change)="opChecked(prod, i)"
                          [attr.aria-label]="'Seleziona ' + prod.op_code"
                        />
                      </label>

                      <div class="product-row__text">
                        <span class="product-row__code">{{ prod.op_code }}</span>
                        <span class="product-row__desc">{{ prod.op_description }}</span>
                      </div>

                      <div class="product-row__qty">
                        <label class="product-row__qty-label" [attr.for]="'qty-' + i">
                          Qty
                        </label>
                        <input
                          [id]="'qty-' + i"
                          type="number"
                          min="0"
                          [max]="prod.op_qta"
                          [value]="prod.op_qta_picked"
                          (input)="updateProduct($any($event.target).value, i)"
                          class="product-row__qty-input"
                        />
                        <span class="product-row__qty-total">/ {{ prod.op_qta }}</span>
                      </div>

                      @if (prod.op_qta > (prod.op_stock ?? 0)) {
                        <span class="product-row__warn" title="Stock insufficiente">⚠️</span>
                      }
                    </li>
                  }
                </ul>
              }

            </section>
          </form>
        }

      </div>

      <!-- ─── Footer ─────────────────────────────────────────────── -->
      @if (orderNew) {
        <footer class="modal__footer">
          <button type="button" class="btn btn--ghost" (click)="goBack()">Annulla</button>
          <button
            type="button"
            class="btn btn--primary"
            (click)="addOrderMethod(assignOrderForm)"
            [disabled]="assignOrderForm.invalid || loading"
          >
            @if (loading) { <span class="btn-spinner"></span> }
            ⏱ Pronto per l'operatore
          </button>
        </footer>
      }

    </aside>
  </div>
}

<!--
  Legacy sub-modal `<app-product-detail>` removed from this template
  in v2.6.0 — the legacy template wired it to a "search similar
  products" flow that the new template doesn't expose (the assign
  modal now just shows the order's own products, no inline product
  search). If the search-similar flow is ever needed again, it
  should land as its own sub-route or dedicated dialog, not as a
  ghost subscriber on `openModal.isProdopen`.
-->

